import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, Calendar, Check, ClipboardCheck, Copy, Plus, UserCheck, X } from "lucide-react";

const ACTION_LABELS = {
  coaching: "Coaching & Counseling",
  verbal: "Verbal Warning",
  written: "Written Warning",
  suspension: "Suspension",
  final: "Final Warning",
  termination: "Termination",
};
const ISSUE_LABELS = {
  absenteeism: "Absenteeism",
  policy_violation: "Policy / procedure violation",
  conduct: "Conduct",
  unsatisfactory_performance: "Unsatisfactory performance",
  safety_violation: "Safety violation",
  other: "Other",
};
const REVIEW_LABELS = {
  "30_day": "30-day",
  "60_day": "60-day",
  "90_day": "90-day",
  annual: "Annual",
  custom: "Custom",
};

/* ═════ MAIN TAB ═════ */
export default function ReviewsDisciplineTab({ employee, canEditFull }) {
  const [records, setRecords] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reference, setReference] = useState(null);
  const [openDisciplineNew, setOpenDisciplineNew] = useState(false);
  const [openReviewNew, setOpenReviewNew] = useState(false);
  const [detail, setDetail] = useState(null);  // { kind, record }

  const load = async () => {
    const [d, r] = await Promise.all([
      api.get(`/discipline/employee/${employee.user_id || employee.id}`),
      api.get(`/reviews/employee/${employee.user_id || employee.id}`),
    ]);
    setRecords(d.data.records || []);
    setReviews(r.data.reviews || []);
  };
  useEffect(() => {
    load();
    api.get("/discipline/reference").then((r) => setReference(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [employee.user_id, employee.id]);

  return (
    <div className="space-y-6" data-testid="tab-content-reviews">
      {/* Discipline section */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-serif-display text-lg text-stone-900">Discipline</h3>
            <div className="label-eyebrow mt-0.5">{records.length} record{records.length !== 1 ? "s" : ""}</div>
          </div>
          {canEditFull && (
            <button
              onClick={() => setOpenDisciplineNew(true)}
              data-testid="rd-new-discipline"
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-800"
            >
              <Plus size={12} /> Log discipline
            </button>
          )}
        </div>
        {records.length === 0 ? (
          <div className="text-xs text-stone-400 italic border border-dashed border-stone-200 rounded-2xl px-4 py-6 text-center" data-testid="rd-discipline-empty">
            No discipline on file. A clean record is a good record.
          </div>
        ) : (
          <div className="space-y-2">
            {records.map((r) => (
              <DisciplineRow key={r.id} record={r} onOpen={() => setDetail({ kind: "discipline", record: r })} />
            ))}
          </div>
        )}
      </section>

      {/* Reviews section */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-serif-display text-lg text-stone-900">Reviews</h3>
            <div className="label-eyebrow mt-0.5">{reviews.length} scheduled/completed</div>
          </div>
          {canEditFull && (
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  try {
                    const r = await api.post(`/reviews/auto-schedule/${employee.user_id || employee.id}`);
                    if (r.data.created.length === 0) {
                      toast.info(`All 30/60/90 already scheduled (${r.data.skipped.join(", ")}).`);
                    } else {
                      toast.success(`Scheduled: ${r.data.created.join(", ")}`);
                    }
                    load();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Auto-schedule failed");
                  }
                }}
                data-testid="rd-auto-schedule"
                className="flex items-center gap-1 px-3 py-1.5 rounded-full subtle-border text-xs hover:bg-stone-50"
                title="Auto-schedule 30/60/90-day reviews from hire date"
              >
                <Calendar size={12} /> 30/60/90
              </button>
              <button
                onClick={() => setOpenReviewNew(true)}
                data-testid="rd-new-review"
                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-800"
              >
                <Plus size={12} /> Schedule review
              </button>
            </div>
          )}
        </div>
        {reviews.length === 0 ? (
          <div className="text-xs text-stone-400 italic border border-dashed border-stone-200 rounded-2xl px-4 py-6 text-center" data-testid="rd-reviews-empty">
            No reviews scheduled yet.
          </div>
        ) : (
          <div className="space-y-2">
            {reviews.map((r) => (
              <ReviewRow key={r.id} review={r} onOpen={() => setDetail({ kind: "review", record: r })} />
            ))}
          </div>
        )}
      </section>

      {openDisciplineNew && reference && (
        <NewDisciplineDialog
          employee={employee}
          reference={reference}
          onClose={() => setOpenDisciplineNew(false)}
          onCreated={(r) => { setOpenDisciplineNew(false); load(); setDetail({ kind: "discipline", record: r }); }}
        />
      )}
      {openReviewNew && reference && (
        <NewReviewDialog
          employee={employee}
          reference={reference}
          onClose={() => setOpenReviewNew(false)}
          onCreated={(r) => { setOpenReviewNew(false); load(); setDetail({ kind: "review", record: r }); }}
        />
      )}
      {detail && (
        <RecordDetailDialog
          kind={detail.kind}
          record={detail.record}
          canEdit={canEditFull}
          onClose={() => { setDetail(null); load(); }}
        />
      )}
    </div>
  );
}

/* ═════ ROW COMPONENTS ═════ */
function DisciplineRow({ record, onOpen }) {
  const sigCount = ["employee", "witness", "mgmt"].filter((k) => record.signatures?.[k]).length;
  const complete = sigCount >= 2;   // mgmt + employee at minimum
  const expired = record.is_expired;
  return (
    <button
      onClick={onOpen}
      data-testid={`rd-record-${record.id}`}
      className={`w-full flex items-center justify-between p-3 rounded-2xl subtle-border hover:bg-stone-50 text-left transition-colors ${expired ? "opacity-60" : ""}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <AlertTriangle size={14} className={expired ? "text-stone-400" : complete ? "text-emerald-600" : "text-amber-600"} />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-stone-900 truncate flex items-center gap-2">
            <span className="truncate">{ACTION_LABELS[record.action_type]} · {ISSUE_LABELS[record.issue_category]}</span>
            {expired && <span data-testid={`rd-expired-${record.id}`} className="text-[9px] uppercase tracking-widest bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full shrink-0">Expired</span>}
          </div>
          <div className="text-[11px] text-stone-500 truncate">
            {record.incident_date}
            {record.sop_reference && ` · SOP: ${record.sop_reference}`}
            {" · "}logged by {record.logged_by_name || "—"}
            {record.expires_at && !expired && ` · expires ${record.expires_at.slice(0, 10)}`}
          </div>
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-stone-400 shrink-0 ml-2">
        {sigCount}/3 signed
      </div>
    </button>
  );
}

function ReviewRow({ review, onOpen }) {
  const overdue = new Date(review.due_date) < new Date() && review.status !== "completed";
  return (
    <button
      onClick={onOpen}
      data-testid={`rd-review-${review.id}`}
      className="w-full flex items-center justify-between p-3 rounded-2xl subtle-border hover:bg-stone-50 text-left transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <ClipboardCheck size={14} className={review.status === "completed" ? "text-emerald-600" : overdue ? "text-rose-500" : "text-stone-500"} />
        <div className="min-w-0 flex-1">
          <div className="text-sm text-stone-900 truncate">
            {REVIEW_LABELS[review.review_type]} review
          </div>
          <div className="text-[11px] text-stone-500">
            Due {review.due_date}
            {review.status === "completed" && " · completed"}
            {overdue && " · overdue"}
          </div>
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-stone-400 shrink-0 ml-2">
        {review.status}
      </div>
    </button>
  );
}

/* ═════ NEW DISCIPLINE DIALOG ═════ */
function NewDisciplineDialog({ employee, reference, onClose, onCreated }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    action_type: "verbal",
    issue_category: "policy_violation",
    sop_reference: "",
    incident_date: new Date().toISOString().slice(0, 10),
    incident_time: "",
    explanation: "",
    corrective_goals: "",
    next_step: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.explanation.trim() || !form.corrective_goals.trim()) {
      toast.error("Explanation and corrective goals are required.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/discipline/records", {
        employee_id: employee.user_id || employee.id,
        employee_name: employee.name,
        boutique_code: employee.boutique_code || user?.boutique_code || "",
        dept: employee.role || "",
        ...form,
      });
      toast.success("Discipline logged");
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 mobile-modal-wrap" onClick={onClose} data-testid="rd-discipline-dialog">
      <div className="bg-white rounded-3xl w-full max-w-xl shadow-xl max-h-[90vh] overflow-y-auto mobile-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-stone-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-serif-display text-lg text-stone-900">Log discipline</h3>
            <div className="text-[11px] text-stone-500 mt-0.5">For {employee.name} · {employee.boutique_code || user?.boutique_code}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-100"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FieldWrap label="Action taken">
              <Select value={form.action_type} onChange={(e) => set("action_type", e.target.value)} testid="rd-action-type">
                {reference.action_types.map((a) => <option key={a} value={a}>{ACTION_LABELS[a]}</option>)}
              </Select>
            </FieldWrap>
            <FieldWrap label="Issue category">
              <Select value={form.issue_category} onChange={(e) => set("issue_category", e.target.value)} testid="rd-issue-cat">
                {reference.issue_categories.map((c) => <option key={c} value={c}>{ISSUE_LABELS[c]}</option>)}
              </Select>
            </FieldWrap>
          </div>
          {form.issue_category === "policy_violation" && (
            <FieldWrap label="SOP violated">
              <Select value={form.sop_reference} onChange={(e) => set("sop_reference", e.target.value)} testid="rd-sop">
                <option value="">— pick SOP —</option>
                {reference.sop_references.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </FieldWrap>
          )}
          <div className="grid grid-cols-2 gap-3">
            <FieldWrap label="Date of occurrence">
              <input type="date" value={form.incident_date} onChange={(e) => set("incident_date", e.target.value)} data-testid="rd-inc-date" className="w-full px-3 py-1.5 rounded-2xl subtle-border text-xs outline-none focus:border-stone-400" />
            </FieldWrap>
            <FieldWrap label="Time (optional)">
              <input type="time" value={form.incident_time} onChange={(e) => set("incident_time", e.target.value)} className="w-full px-3 py-1.5 rounded-2xl subtle-border text-xs outline-none focus:border-stone-400" />
            </FieldWrap>
          </div>
          <FieldWrap label="Explanation (what happened)">
            <textarea value={form.explanation} onChange={(e) => set("explanation", e.target.value)} rows={4} data-testid="rd-explanation" className="w-full px-3 py-2 rounded-2xl subtle-border text-xs outline-none focus:border-stone-400" placeholder="Details of occurrences that have led to disciplinary action…" />
          </FieldWrap>
          <FieldWrap label="Goals / corrective behavior">
            <textarea value={form.corrective_goals} onChange={(e) => set("corrective_goals", e.target.value)} rows={3} data-testid="rd-goals" className="w-full px-3 py-2 rounded-2xl subtle-border text-xs outline-none focus:border-stone-400" placeholder="Necessary actions to correct behaviors…" />
          </FieldWrap>
          <FieldWrap label="Next step if not corrected (optional)">
            <Select value={form.next_step} onChange={(e) => set("next_step", e.target.value)}>
              <option value="">—</option>
              <option value="written">Written warning</option>
              <option value="final">Final warning</option>
              <option value="termination">Termination</option>
            </Select>
          </FieldWrap>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-stone-100 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-xs text-stone-600 hover:bg-stone-100">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="rd-discipline-save" className="px-5 py-2 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-800 disabled:opacity-40">
            {busy ? "Saving…" : "Save & continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═════ NEW REVIEW DIALOG ═════ */
function NewReviewDialog({ employee, reference, onClose, onCreated }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    review_type: "annual",
    due_date: new Date().toISOString().slice(0, 10),
    template_notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/reviews", {
        employee_id: employee.user_id || employee.id,
        employee_name: employee.name,
        boutique_code: employee.boutique_code || user?.boutique_code || "",
        ...form,
      });
      toast.success("Review scheduled");
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 mobile-modal-wrap" onClick={onClose} data-testid="rd-review-dialog">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-xl mobile-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-stone-100 px-6 py-4 flex items-center justify-between">
          <h3 className="font-serif-display text-lg text-stone-900">Schedule review</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-100"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FieldWrap label="Type">
              <Select value={form.review_type} onChange={(e) => set("review_type", e.target.value)} testid="rd-review-type">
                {reference.review_types.map((t) => <option key={t} value={t}>{REVIEW_LABELS[t]}</option>)}
              </Select>
            </FieldWrap>
            <FieldWrap label="Due date">
              <input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="rd-review-due" className="w-full px-3 py-1.5 rounded-2xl subtle-border text-xs outline-none focus:border-stone-400" />
            </FieldWrap>
          </div>
          <FieldWrap label="Notes for this review (optional)">
            <textarea value={form.template_notes} onChange={(e) => set("template_notes", e.target.value)} rows={4} className="w-full px-3 py-2 rounded-2xl subtle-border text-xs outline-none focus:border-stone-400" placeholder="Structured templates coming in Phase 2 — for now, freeform notes." />
          </FieldWrap>
        </div>
        <div className="border-t border-stone-100 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-xs text-stone-600 hover:bg-stone-100">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="rd-review-save" className="px-5 py-2 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-800 disabled:opacity-40">
            {busy ? "Saving…" : "Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═════ RECORD DETAIL — sign panel + witness invite + complete review ═════ */
function RecordDetailDialog({ kind, record: initial, canEdit, onClose }) {
  const [record, setRecord] = useState(initial);
  const [signingRole, setSigningRole] = useState(null);      // "employee" | "witness" | "mgmt"
  const [typedName, setTypedName] = useState("");
  const [witnessBusy, setWitnessBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [chosenWitness, setChosenWitness] = useState("");
  const [magicLink, setMagicLink] = useState("");
  const [openComplete, setOpenComplete] = useState(false);

  useEffect(() => {
    if (canEdit) {
      api.get("/employees").then((r) => {
        const list = (r.data || []).map((e) => ({
          id: e.user_id || e.id,
          name: e.name,
          boutique_code: e.boutique_code,
          role: e.role,
        }));
        setUsers(list);
      }).catch(() => {});
    }
  }, [canEdit]);

  const managerRoles = new Set(["boutique_manager", "gm", "owner", "recruiter", "leadership", "district_manager"]);
  const managerList = users.filter((u) => managerRoles.has((u.role || "").toLowerCase()));

  const kindPath = kind === "discipline" ? "discipline" : "reviews";

  const sign = async (role) => {
    if (!typedName.trim()) { toast.error("Type your full name to sign."); return; }
    try {
      const r = await api.post(`/${kindPath}/${record.id}/sign`, { role, typed_name: typedName.trim() });
      setRecord(r.data);
      setSigningRole(null); setTypedName("");
      toast.success(`Signed as ${role}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sign failed");
    }
  };

  const inviteWitness = async (role = "witness") => {
    if (!chosenWitness) { toast.error("Pick a signer first."); return; }
    setWitnessBusy(true);
    try {
      const w = users.find((u) => u.id === chosenWitness);
      const r = await api.post(`/${kindPath}/${record.id}/witness/invite`, {
        witness_user_id: chosenWitness,
        witness_name: w?.name || "",
        witness_boutique: w?.boutique_code || "",
        role,
      });
      const fullUrl = `${window.location.origin}${r.data.witness_url}`;
      setMagicLink(fullUrl);
      if (role === "witness") {
        setRecord((rec) => ({ ...rec, witness_name: w?.name, witness_user_id: chosenWitness }));
      }
      toast.success(`${role[0].toUpperCase() + role.slice(1)} link ready — copy and send.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    } finally { setWitnessBusy(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(magicLink);
    toast.success("Copied to clipboard");
  };

  const sigs = record.signatures || {};

  return (
    <div className="fixed inset-0 z-[60] bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 mobile-modal-wrap" onClick={onClose} data-testid="rd-detail-dialog">
      <div className="bg-white rounded-3xl w-full max-w-2xl shadow-xl max-h-[92vh] overflow-y-auto mobile-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-stone-100 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">{kind === "discipline" ? "Disciplinary Action" : "Employee Review"}</div>
            <h3 className="font-serif-display text-lg text-stone-900">
              {kind === "discipline" ? ACTION_LABELS[record.action_type] : `${REVIEW_LABELS[record.review_type]} review`}
              {" · "}{record.employee_name}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-100"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-5">
          {kind === "discipline" ? (
            <>
              <MetaRow label="Issue" value={ISSUE_LABELS[record.issue_category]} />
              {record.sop_reference && <MetaRow label="SOP" value={record.sop_reference} />}
              <MetaRow label="Occurred" value={`${record.incident_date}${record.incident_time ? " · " + record.incident_time : ""}`} />
              <Block label="Explanation" value={record.explanation} />
              <Block label="Goals / corrective behavior" value={record.corrective_goals} />
              {record.next_step && <MetaRow label="Next step" value={record.next_step} />}
            </>
          ) : (
            <>
              <MetaRow label="Due" value={record.due_date} />
              <MetaRow label="Status" value={record.status} />
              {record.template_key && <MetaRow label="Template" value={record.template_key} />}
              {record.template_notes && <Block label="Notes" value={record.template_notes} />}
              {record.overall?.total !== undefined && (
                <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
                  <div className="label-eyebrow text-emerald-800 mb-1">Overall</div>
                  <div className="flex items-baseline gap-3">
                    <div className="font-serif-display text-2xl text-emerald-900">{record.overall.total} / {record.overall.max}</div>
                    <div className="text-sm text-emerald-800">{record.overall.band}</div>
                    {record.overall.rate_increase !== null && (
                      <div className="text-xs text-emerald-700 ml-auto">
                        Suggested rate increase: ${record.overall.rate_increase.toFixed(2)}/service
                      </div>
                    )}
                  </div>
                </div>
              )}
              {record.responses && (
                <>
                  {record.responses.strengths && <Block label="Strengths" value={record.responses.strengths} />}
                  {record.responses.improvement_areas && <Block label="Areas needing improvement" value={record.responses.improvement_areas} />}
                  {record.responses.performance_goals && <Block label="Performance goals" value={record.responses.performance_goals} />}
                  {record.responses.recognition && <Block label="Recognition / reward" value={record.responses.recognition} />}
                  {record.responses.interests && <Block label="Areas of interest" value={record.responses.interests} />}
                  {record.responses.employee_comments && <Block label="Employee comments" value={record.responses.employee_comments} />}
                </>
              )}
              {canEdit && record.status !== "completed" && (
                <button
                  onClick={() => setOpenComplete(true)}
                  data-testid="rd-complete-review"
                  className="w-full mt-2 px-4 py-2 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-800"
                >
                  {record.template_key ? `Fill out ${record.template_key === "mt_annual" ? "MT Annual" : "template"}` : "Complete review"}
                </button>
              )}
            </>
          )}

          {/* Signatures */}
          <div className="pt-3 border-t border-stone-100 space-y-3">
            <div className="label-eyebrow">Signatures</div>
            {["employee", "witness", "mgmt"].map((role) => (
              <SignatureLine
                key={role}
                role={role}
                sig={sigs[role]}
                witnessName={role === "witness" ? record.witness_name : null}
                canEdit={canEdit}
                signingRole={signingRole}
                onStart={() => setSigningRole(role)}
                onCancel={() => { setSigningRole(null); setTypedName(""); }}
                onSubmit={() => sign(role)}
                typedName={typedName}
                setTypedName={setTypedName}
              />
            ))}
          </div>

          {/* Magic-link invites (witness + employee) */}
          {canEdit && (!sigs.witness || !sigs.employee) && (
            <div className="pt-3 border-t border-stone-100 space-y-4">
              {!sigs.witness && (
                <div>
                  <div className="label-eyebrow mb-2 flex items-center gap-1.5"><UserCheck size={11} /> Send a witness magic-link (cross-boutique GM, etc.)</div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      value={chosenWitness}
                      onChange={(e) => setChosenWitness(e.target.value)}
                      data-testid="rd-witness-select"
                      className="flex-1 px-3 py-1.5 rounded-full subtle-border text-xs outline-none bg-white"
                    >
                      <option value="">— pick a witness —</option>
                      {managerList.map((u) => {
                        const label = `${u.name} · ${u.boutique_code || "Rainbow"} · ${(u.role || "").replace(/_/g, " ")}`;
                        return <option key={u.id} value={u.id}>{label}</option>;
                      })}
                    </select>
                    <button
                      onClick={() => inviteWitness("witness")}
                      disabled={!chosenWitness || witnessBusy}
                      data-testid="rd-witness-invite"
                      className="px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-800 disabled:opacity-40"
                    >
                      {witnessBusy ? "…" : "Generate witness link"}
                    </button>
                  </div>
                </div>
              )}

              {!sigs.employee && (
                <div>
                  <div className="label-eyebrow mb-2 flex items-center gap-1.5"><UserCheck size={11} /> Or send the employee their own magic-link</div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1 px-3 py-1.5 rounded-full subtle-border text-xs text-stone-700 bg-stone-50">
                      {record.employee_name}
                    </div>
                    <button
                      onClick={() => {
                        // Employee signs themselves — use their own record ref as signer id
                        setChosenWitness(record.employee_id);
                        setTimeout(() => inviteWitness("employee"), 50);
                      }}
                      disabled={witnessBusy}
                      data-testid="rd-employee-invite"
                      className="px-4 py-1.5 rounded-full subtle-border text-xs hover:bg-stone-50 disabled:opacity-40"
                    >
                      {witnessBusy ? "…" : "Generate employee link"}
                    </button>
                  </div>
                </div>
              )}

              {magicLink && (
                <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-between gap-2">
                  <code className="text-[11px] text-emerald-900 truncate flex-1" data-testid="rd-witness-link">{magicLink}</code>
                  <button onClick={copyLink} className="px-3 py-1 rounded-full bg-emerald-700 text-white text-[11px] flex items-center gap-1 shrink-0">
                    <Copy size={11} /> Copy
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Complete review dialog (opens from the review's detail view) */}
      {openComplete && (
        <CompleteReviewDialog
          review={record}
          onClose={() => setOpenComplete(false)}
          onCompleted={(r) => { setRecord(r); setOpenComplete(false); toast.success("Review completed"); }}
        />
      )}
    </div>
  );
}

/* ═════ COMPLETE REVIEW DIALOG — MT annual template or freeform ═════ */
function CompleteReviewDialog({ review, onClose, onCompleted }) {
  const [tpl, setTpl] = useState(null);
  const [areaScores, setAreaScores] = useState({});
  const [narratives, setNarratives] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (review.template_key) {
      api.get(`/reviews/templates/${review.template_key}`)
        .then((r) => setTpl(r.data))
        .catch(() => setTpl(null));
    }
  }, [review.template_key]);

  const total = tpl ? tpl.areas.reduce((sum, a) => sum + (Number(areaScores[a.key]) || 0), 0) : 0;
  const band = tpl ? tpl.bands.find((b) => total >= b.min && total <= b.max) : null;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = tpl
        ? { area_scores: areaScores, ...narratives, template_key: tpl.key }
        : narratives;
      const r = await api.post(`/reviews/${review.id}/complete`, payload);
      onCompleted(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4 mobile-modal-wrap" onClick={onClose} data-testid="rd-complete-dialog">
      <div className="bg-white rounded-3xl w-full max-w-3xl shadow-xl max-h-[95vh] overflow-y-auto mobile-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-stone-100 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-400">{tpl?.name || "Review"}</div>
            <h3 className="font-serif-display text-lg text-stone-900">{review.employee_name}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-100"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-6">
          {tpl && (
            <>
              <div className="text-xs text-stone-500 italic leading-relaxed">
                Appraise employee for performance during this review period only. Consider each factor based on consistent, objective standards.
              </div>

              <div className="space-y-2">
                {tpl.areas.map((a, i) => (
                  <div key={a.key} className="rounded-2xl subtle-border p-4" data-testid={`rev-area-${a.key}`}>
                    <div className="mb-2">
                      <div className="text-sm text-stone-900 font-medium">{i + 1}. {a.label}</div>
                      <div className="text-[11px] text-stone-500 mt-0.5 leading-relaxed">{a.prompt}</div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {tpl.scale.map((s) => (
                        <button
                          key={s.value}
                          onClick={() => setAreaScores((prev) => ({ ...prev, [a.key]: s.value }))}
                          data-testid={`rev-score-${a.key}-${s.value}`}
                          className={`px-3 py-1.5 rounded-full text-[11px] transition-colors ${
                            areaScores[a.key] === s.value ? "bg-stone-900 text-white" : "subtle-border text-stone-600 hover:bg-stone-50"
                          }`}
                        >
                          {s.value} · {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4" data-testid="rev-overall">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <div className="label-eyebrow text-emerald-800">Overall</div>
                  <div className="font-serif-display text-2xl text-emerald-900">{total} / {tpl.max_score}</div>
                  {band && <div className="text-sm text-emerald-800">{band.label}</div>}
                  {band && band.rate_increase > 0 && (
                    <div className="text-xs text-emerald-700 ml-auto">Suggested rate increase: ${band.rate_increase.toFixed(2)}/service</div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {tpl.narratives.map((n) => (
                  <label key={n.key} className="block">
                    <div className="label-eyebrow mb-1">{n.label}</div>
                    {n.hint && <div className="text-[11px] text-stone-400 italic mb-1">{n.hint}</div>}
                    <textarea
                      value={narratives[n.key] || ""}
                      onChange={(e) => setNarratives((prev) => ({ ...prev, [n.key]: e.target.value }))}
                      rows={3}
                      data-testid={`rev-narr-${n.key}`}
                      className="w-full px-3 py-2 rounded-2xl subtle-border text-xs outline-none focus:border-stone-400"
                    />
                  </label>
                ))}
              </div>
            </>
          )}

          {!tpl && (
            <>
              <div className="text-xs text-stone-500 italic">Freeform review — no structured template attached.</div>
              <label className="block">
                <div className="label-eyebrow mb-1">Strengths</div>
                <textarea rows={3} onChange={(e) => setNarratives((p) => ({ ...p, strengths: e.target.value }))} className="w-full px-3 py-2 rounded-2xl subtle-border text-xs outline-none" />
              </label>
              <label className="block">
                <div className="label-eyebrow mb-1">Growth areas</div>
                <textarea rows={3} onChange={(e) => setNarratives((p) => ({ ...p, growth_areas: e.target.value }))} className="w-full px-3 py-2 rounded-2xl subtle-border text-xs outline-none" />
              </label>
              <label className="block">
                <div className="label-eyebrow mb-1">Goals</div>
                <textarea rows={3} onChange={(e) => setNarratives((p) => ({ ...p, goals: e.target.value }))} className="w-full px-3 py-2 rounded-2xl subtle-border text-xs outline-none" />
              </label>
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-stone-100 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-xs text-stone-600 hover:bg-stone-100">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="rev-complete-save" className="px-5 py-2 rounded-full bg-stone-900 text-white text-xs hover:bg-stone-800 disabled:opacity-40">
            {busy ? "Saving…" : "Save & complete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═════ Helpers ═════ */
function FieldWrap({ label, children }) {
  return (
    <label className="block">
      <div className="label-eyebrow mb-1">{label}</div>
      {children}
    </label>
  );
}
function Select({ children, testid, ...rest }) {
  return <select {...rest} data-testid={testid} className="w-full px-3 py-1.5 rounded-2xl subtle-border text-xs outline-none focus:border-stone-400 bg-white">{children}</select>;
}
function MetaRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-3">
      <div className="label-eyebrow w-32 shrink-0">{label}</div>
      <div className="text-sm text-stone-800">{value}</div>
    </div>
  );
}
function Block({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="text-sm text-stone-800 whitespace-pre-wrap leading-relaxed">{value}</div>
    </div>
  );
}
function SignatureLine({ role, sig, witnessName, canEdit, signingRole, onStart, onCancel, onSubmit, typedName, setTypedName }) {
  const label = role === "mgmt" ? "MGMT" : role === "witness" ? "Witness" : "Employee";
  if (sig) {
    return (
      <div className="flex items-center justify-between p-3 rounded-2xl bg-emerald-50 border border-emerald-200" data-testid={`rd-sig-${role}`}>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-emerald-800">{label}</div>
          <div className="text-sm text-stone-900">{sig.typed_name}</div>
          <div className="text-[10px] text-stone-500">Signed {new Date(sig.signed_at).toLocaleString()} · {sig.mode === "magic_link" ? "via link" : "in-person"}</div>
        </div>
        <Check size={16} className="text-emerald-700" />
      </div>
    );
  }
  if (signingRole === role && canEdit) {
    return (
      <div className="p-3 rounded-2xl border border-stone-300 bg-stone-50 space-y-2" data-testid={`rd-sig-input-${role}`}>
        <div className="text-[11px] uppercase tracking-widest text-stone-500">
          {label}{role === "witness" && witnessName ? ` (${witnessName})` : ""}
        </div>
        <input
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          placeholder={`Type ${role === "mgmt" ? "manager's" : role === "witness" ? "witness'" : "employee's"} full name`}
          data-testid={`rd-sig-input-field-${role}`}
          className="w-full px-3 py-1.5 rounded-2xl subtle-border text-xs outline-none focus:border-stone-400 font-serif italic bg-white"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-1 rounded-full text-[11px] text-stone-500 hover:bg-stone-100">Cancel</button>
          <button onClick={onSubmit} data-testid={`rd-sig-confirm-${role}`} className="px-4 py-1 rounded-full bg-stone-900 text-white text-[11px]">Sign</button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between p-3 rounded-2xl border border-dashed border-stone-200" data-testid={`rd-sig-pending-${role}`}>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-stone-400">{label}{role === "witness" && witnessName ? ` (${witnessName})` : ""}</div>
        <div className="text-xs text-stone-500 italic">Not signed{role === "witness" && !witnessName ? " — no witness assigned" : ""}</div>
      </div>
      {canEdit && (
        <button onClick={onStart} data-testid={`rd-sig-start-${role}`} className="px-3 py-1 rounded-full text-[11px] subtle-border hover:bg-stone-50">Sign in-person</button>
      )}
    </div>
  );
}
